import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/query-keys";

/**
 * Fetches the roles for a given user_id (auth uid) via the get_user_badges RPC.
 * Returns an array of role strings like ["admin", "moderator"].
 */
export function useUserBadges(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.user.badges(userId!),
    queryFn: async () => {
      if (!userId) return [] as any[];
      const { data, error } = await supabase.rpc("get_user_badges", {
        p_user_id: userId,
      });
      if (error) {
        console.error("Error fetching user badges:", error);
        return [] as any[];
      }
      return (data as string[]) || [];
    },
    enabled: !!userId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Batch fetch roles for multiple user_ids. Returns a map of userId -> roles[].
 */
export function useUserBadgesBatch(userIds: string[]) {
  const uniqueIds = [...new Set(userIds.filter(Boolean))];

  return useQuery({
    queryKey: queryKeys.user.badgesBatch(uniqueIds.sort().join(",")),
    queryFn: async () => {
      const result: Record<string, string[]> = {};
      // Fetch in parallel
      await Promise.all(
        uniqueIds.map(async (uid) => {
          const { data } = await supabase.rpc("get_user_badges", {
            p_user_id: uid,
          });
          result[uid] = (data as string[]) || [];
        })
      );
      return result;
    },
    enabled: uniqueIds.length > 0,
    staleTime: 10 * 60 * 1000,
  });
}
